import React, { useState, useEffect } from 'react';
import './Booking.css';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    roomType: 'single',
  });

  const [errors, setErrors] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  // show a submit error message
  const [submitError, setSubmitError] = useState('');
  const [bookedDates, setBookedDates] = useState([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(true);

  // Get today's date in YYYY-MM-DD format for validation and min attributes
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const url = process.env.REACT_APP_AVAILABILITY_URL;
        if (!url || url.includes('your-endpoint')) {
          console.warn('Availability URL not configured. Using empty booked dates.');
          setBookedDates([]);
          setIsLoadingAvailability(false);
          return;
        }

        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch availability');
        const data = await res.json();
        // Expecting data to be an array of objects: [{ start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }]
        setBookedDates(data || []);
      } catch (err) {
        console.error('Error fetching availability:', err);
      } finally {
        setIsLoadingAvailability(false);
      }
    };

    fetchAvailability();
  }, []);

  const isDateRangeBlocked = (start, end) => {
    const checkIn = new Date(start);
    const checkOut = new Date(end);

    return bookedDates.some(booking => {
      const bookedStart = new Date(booking.start);
      const bookedEnd = new Date(booking.end);

      // Overlap logic: (StartA <= EndB) and (EndA >= StartB)
      return (checkIn < bookedEnd) && (checkOut > bookedStart);
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for the current field when user types
    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (!formData.checkIn) {
      newErrors.checkIn = 'Check-in date is required';
      isValid = false;
    } else if (new Date(formData.checkIn) < new Date(today)) {
      newErrors.checkIn = 'Check-in date cannot be in the past';
      isValid = false;
    }

    if (!formData.checkOut) {
      newErrors.checkOut = 'Check-out date is required';
      isValid = false;
    } else if (new Date(formData.checkOut) <= new Date(formData.checkIn)) {
      newErrors.checkOut = 'Check-out date must be after check-in date';
      isValid = false;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
      isValid = false;
    }

    if (isValid && isDateRangeBlocked(formData.checkIn, formData.checkOut)) {
      setSubmitError('Sorry, some of these dates are already booked. Please choose different dates.');
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowSuccessMessage(false);
    setSubmitError('');

    if (validateForm()) {
      try {
        const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_URL;

        if (!webhookUrl || webhookUrl.includes('your-endpoint')) {
          console.warn('n8n Webhook URL not configured. Falling back to console log for development.');
          console.log('Form Data:', formData);
          setShowSuccessMessage(true);
          return;
        }

        const payload = {
          type: 'booking',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          checkIn: formData.checkIn,
          checkOut: formData.checkOut,
          roomType: formData.roomType,
          submittedAt: new Date().toISOString()
        };

        const res = await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error('Failed to send booking request');

        setShowSuccessMessage(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          checkIn: '',
          checkOut: '',
          roomType: 'single',
        });
      } catch (err) {
        console.error('Booking submission error:', err);
        setSubmitError('Failed to send booking request. Please try again or contact us directly.');
      }
    }
  };



  return (
    <section className="booking-section" id="booking">
      <h2>Book Your Stay</h2>
      <p className="booking-intro">
        Experience luxury and comfort. Fill out the form below to book your stay at Suit360.
      </p>
      <div className="booking-form">
        {showSuccessMessage && (
          <div className="success-message">
            <p>Thank you for your booking! We have received your request and will get back to you shortly.</p>
          </div>
        )}
        {submitError && (
          <div className="error-message-box">
            <p>{submitError}</p>
          </div>
        )}
        {isLoadingAvailability && (
          <div className="availability-loading">
            <p>Checking availability...</p>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'input-error' : ''}
            />
            {errors.name && <p className="error-message">{errors.name}</p>}
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'input-error' : ''}
            />
            {errors.email && <p className="error-message">{errors.email}</p>}
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="e.g. +234..."
              className={errors.phone ? 'input-error' : ''}
            />
            {errors.phone && <p className="error-message">{errors.phone}</p>}
          </div>
          <div className="form-group">
            <label htmlFor="checkIn">Check-In Date</label>
            <input
              type="date"
              id="checkIn"
              name="checkIn"
              value={formData.checkIn}
              onChange={handleChange}
              min={today}
              className={errors.checkIn ? 'input-error' : ''}
            />
            {errors.checkIn && <p className="error-message">{errors.checkIn}</p>}
          </div>
          <div className="form-group">
            <label htmlFor="checkOut">Check-Out Date</label>
            <input
              type="date"
              id="checkOut"
              name="checkOut"
              value={formData.checkOut}
              onChange={handleChange}
              min={formData.checkIn || today} // Check-out must be after check-in, or today if check-in is not set
              className={errors.checkOut ? 'input-error' : ''}
            />
            {errors.checkOut && <p className="error-message">{errors.checkOut}</p>}
          </div>
          {/*
          <div className="form-group">
            <label htmlFor="roomType">Room Type</label>
            <select
              id="roomType"
              name="roomType"
              value={formData.roomType}
              onChange={handleChange}
            >
              <option value="single">Single</option>
              <option value="double">Double</option>
              <option value="suite">Suite</option>
            </select>
          </div>
          */}
          <button type="submit" className="btn-primary" disabled={isLoadingAvailability}>
            Request Booking
          </button>
          {submitError && <p className="error-message">{submitError}</p>}
        </form>
      </div>
    </section>
  );
};

export default Booking;
